import { SowInput, SowTextarea } from './ValidationFields';
import { Button } from '../Button';
import type { SowCoachingPhase } from '../../types';

interface Props {
  phases: SowCoachingPhase[];
  contractedHours: number;
  onChange: (next: SowCoachingPhase[]) => void;
}

/**
 * Project Approach editor: per-phase name / hours / description with a live
 * sum check against the contracted hours (the document's phase hours must add
 * up — Ariel's SOWs state "estimated consulting time" per phase).
 */
export function PhaseHoursEditor({ phases, contractedHours, onChange }: Props): JSX.Element {
  const sum = Math.round(phases.reduce((s, p) => s + (Number.isFinite(p.hours) ? p.hours : 0), 0) * 100) / 100;
  const mismatch = Math.abs(sum - contractedHours) > 0.001;

  const update = (i: number, patch: Partial<SowCoachingPhase>) => {
    onChange(phases.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  };
  const remove = (i: number) => onChange(phases.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= phases.length) return;
    const next = [...phases];
    const a = next[i];
    const b = next[j];
    if (!a || !b) return;
    next[i] = b;
    next[j] = a;
    onChange(next);
  };

  return (
    <div>
      {phases.map((p, i) => (
        <div key={i} className="card" style={{ marginBottom: 8, padding: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px auto', gap: 8, alignItems: 'center' }}>
            <SowInput
              className="input"
              placeholder="Phase name"
              name={`phases.${i}.name`} value={p.name}
              onChange={(e) => update(i, { name: e.target.value })}
            />
            <SowInput
              className="input d-tnum"
              type="number"
              min={0}
              step={0.5}
              name={`phases.${i}.hours`} value={Number.isFinite(p.hours) ? p.hours : ''}
              onChange={(e) => update(i, { hours: e.target.value === '' ? 0 : Number(e.target.value) })}
              aria-label="Estimated consulting time (hours)"
            />
            <div style={{ display: 'flex', gap: 2 }}>
              <Button size="sm" variant="ghost" icon="chevronLeft" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">{''}</Button>
              <Button size="sm" variant="ghost" icon="chevronRight" onClick={() => move(i, 1)} disabled={i === phases.length - 1} title="Move down">{''}</Button>
              <Button size="sm" variant="ghost" icon="trash" onClick={() => remove(i)} title="Remove phase">{''}</Button>
            </div>
          </div>
          <SowTextarea
            className="input"
            rows={2}
            style={{ width: '100%', height: 'auto', minHeight: 56, padding: '8px 10px', marginTop: 8, resize: 'vertical', lineHeight: 1.5 }}
            placeholder="Phase description (appears verbatim in the document)"
            name={`phases.${i}.description`} value={p.description}
            onChange={(e) => update(i, { description: e.target.value })}
          />
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
        <Button
          size="sm"
          variant="secondary"
          icon="plus"
          onClick={() => onChange([...phases, { name: '', description: '', hours: 0 }])}
        >
          Add phase
        </Button>
        <span className="d-tnum" style={{ fontSize: 13, color: mismatch ? 'var(--warn)' : 'var(--mute)' }}>
          Σ {sum} of {contractedHours} contracted hours
        </span>
      </div>

      {mismatch && (
        <div className="banner warn" style={{ marginTop: 8 }}>
          <span className="dot" />
          <span>
            Phase hours add up to <strong>{sum}</strong> but the contracted time is <strong>{contractedHours}</strong> — the
            document will show both as-is.
          </span>
        </div>
      )}
    </div>
  );
}
